/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const node = feature as BaseNode;

    const incomingLinks = project.getNodeLinks(node, 'cond');
    const trueLinks = project.getNodeLinks(node, 'true_output');
    const falseLinks = project.getNodeLinks(node, 'false_output');
    const expressions: BaseFeature[] = feature.getSettingValue('EXPRESSIONS');
    const source = project.getFeature(incomingLinks[0].getSettingValue('SOURCE_FEATURE')) as BaseNode;
    const sourceParams = source.getPort(incomingLinks[0].getSettingValue('SOURCE_PORT'))?.params;

    let operator = feature.getSettingValue('OPERATOR');
    if (operator === 'AND') {
        operator = '&&';
    } else if (operator === 'OR') {
        operator = '||';
    }
    let code = '';

    const variables = project.getVariables();
    const variableDictionary: { [key: string]: string } = {};
    for (let j = 0; j < variables.length; j += 1) {
        const variable = variables[j];
        variableDictionary[variable.getID()] = variable.getSettingValue('VARNAME');
    }

    for (let j = 0; j < expressions.length; j += 1) {
        const expression: BaseFeature = expressions[j];
        const leftHandOperand = expression.getSettingValue('LEFT_OPERAND');

        let parsedLeftHandOperand = leftHandOperand;
        const leftHandExp = /\{\{(.*?)\}\}/g;
        let match = leftHandExp.exec(leftHandOperand);
        while (match) {
            let varName = `${match[1]}`;
            if (varName.startsWith('$')) {
                varName = varName.slice(1);
                if (variableDictionary[varName]) {
                    varName = variableDictionary[varName];
                }
            }
            parsedLeftHandOperand = parsedLeftHandOperand.replace(match[0], varName);
            match = leftHandExp.exec(parsedLeftHandOperand);
        }
        const expressionOperator = expression.getSettingValue('OPERATOR');
        let languageOperator = expressionOperator;
        switch (expressionOperator) {
            case '=':
                languageOperator = '===';
                break;
            case '<>':
                languageOperator = '!==';
                break;
            default:
                languageOperator = expressionOperator;
        }
        const rightHandOperand = expression.getSettingValue('RIGHT_OPERAND');

        let parsedRightHandOperand = rightHandOperand;
        const rightHandExp = /\{\{(.*?)\}\}/g;
        match = rightHandExp.exec(rightHandOperand);
        while (match) {
            let varName = `${match[1]}`;
            if (varName.startsWith('$')) {
                varName = varName.slice(1);
                if (variableDictionary[varName]) {
                    varName = variableDictionary[varName];
                }
            }
            parsedRightHandOperand = parsedRightHandOperand.replace(match[0], varName);
            match = rightHandExp.exec(parsedRightHandOperand);
        }

        code += `${parsedLeftHandOperand} ${languageOperator} ${parsedRightHandOperand}`;
        if (j !== expressions.length - 1) {
            code += ` ${operator} `;
        }
    }
    for (let i = 0; i < incomingLinks.length; i += 1) {
        const link = incomingLinks[i];

        blocks.push({
            location: link.getSourceId(),
            blockType: BlockType.CODE,
            id: link.getID(),
            template: `
if (${code}) {
    ${trueLinks.length > 0 ? `${project.getCodeLocationStart()}${trueLinks[0].getSourceId()}${project.getCodeLocationEnd()}` : ''}
} else {
    ${falseLinks.length > 0 ? `${project.getCodeLocationStart()}${falseLinks[0].getSourceId()}${project.getCodeLocationEnd()}` : ''}
}`,
        });
    }

    return blocks;
};
